import React from 'react';
import { Minus, Plus, Crosshair, RotateCcw } from 'lucide-react';
import { computeCanonicalRender } from '../../../lib/photoAligner';
import { panBy } from '../../../lib/photoManualTransform';

// ── Canvas sizing ───────────────────────────────────────────────────────────
// The photos are portrait (9:16). A full-width panel on a 1440px desktop is
// ~1200px tall, so the coach had to zoom the whole browser out to see both
// bodies at once. Cap the canvas by viewport height and derive its WIDTH from
// that instead — the pair then always fits one screen. On a phone the `100%`
// branch of min() wins, so mobile layout is unchanged.
//
// 70vh (not more) leaves room for the page chrome the viewer sits under —
// header + "Compare against" select + pose tabs + this component's own toolbar.
export const CANVAS_H = 'min(70vh, 640px)';
export const PANEL_RATIO = 9 / 16;
// Width of a single 9:16 panel at CANVAS_H, and of a two-up pair (+ the gap).
export const SINGLE_W = `min(100%, calc(${CANVAS_H} * ${PANEL_RATIO}))`;
export const PAIR_W = `min(100%, calc(${CANVAS_H} * ${PANEL_RATIO * 2} + 0.25rem))`;

// A 2x2 quad must fit the SAME 70vh budget as a single pair, so each panel
// gets half the height — which makes each column half as wide as in PAIR_W.
// Two columns at CANVAS_H/2 tall are exactly one CANVAS_H-wide-equivalent
// column plus the gap. Without this the quad grid is 140vh tall and the coach
// has to scroll at 1440x900.
export const QUAD_W = `min(100%, calc(${CANVAS_H} * ${PANEL_RATIO} + 0.25rem))`;

// Pointer-drag handler set for panning a photo. Returns {} (no handlers) when
// disabled so the caller can spread it unconditionally.
export function useDragPan(setManual, enabled) {
  const state = React.useRef(null);
  if (!enabled) return {};
  const onPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    state.current = { x: e.clientX, y: e.clientY, w: rect.width, h: rect.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!state.current) return;
    const dx = e.clientX - state.current.x;
    const dy = e.clientY - state.current.y;
    state.current.x = e.clientX;
    state.current.y = e.clientY;
    setManual((m) => panBy(m, dx, dy, state.current.w, state.current.h));
  };
  const onPointerUp = (e) => {
    if (state.current) e.currentTarget.releasePointerCapture?.(e.pointerId);
    state.current = null;
  };
  return {
    onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp,
    style: { cursor: 'grab', touchAction: 'none' },
  };
}

// Press-and-hold repeat for the zoom buttons. Steps are deliberately small
// (3%) so a single tap is a fine nudge; holding covers large moves without
// 20 taps. Returns props to spread on a <button>.
//
// The click handler stays for a plain tap and for keyboard activation
// (Enter/Space fire click, never pointerdown). Once a hold has started
// repeating, the click that arrives on release is swallowed so the last step
// isn't applied twice.
const HOLD_DELAY_MS = 400;
const HOLD_INTERVAL_MS = 80;

export function useHoldRepeat(action) {
  const timers = React.useRef({ delay: null, interval: null, repeated: false });

  const stop = React.useCallback(() => {
    clearTimeout(timers.current.delay);
    clearInterval(timers.current.interval);
    timers.current.delay = null;
    timers.current.interval = null;
  }, []);

  // Never leave a timer running after the panel unmounts (mode switch, dialog close).
  React.useEffect(() => stop, [stop]);

  return {
    onPointerDown: (e) => {
      e.stopPropagation();
      timers.current.repeated = false;
      timers.current.delay = setTimeout(() => {
        timers.current.repeated = true;
        action();
        timers.current.interval = setInterval(action, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);
    },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onClick: () => {
      if (timers.current.repeated) { timers.current.repeated = false; return; }
      action();
    },
  };
}

// Floating pill of alignment controls overlaid on a photo.
// `onAuto` is null when pose data is unavailable (button disabled).
// `onReset` renders only once the photo has actually been nudged.
export function OnPhotoControls({ onZoomOut, onZoomIn, onAuto, onReset, compact = false, autoTitle = 'Pose data unavailable' }) {
  const holdOut = useHoldRepeat(onZoomOut);
  const holdIn = useHoldRepeat(onZoomIn);
  const btn = 'h-11 w-11 flex items-center justify-center rounded-full text-white transition-colors ' +
    'hover:bg-white/15 active:bg-white/25 disabled:opacity-40 disabled:pointer-events-none';
  // Side-by-side panels are only ~177px wide on a phone — a 4th 44px button
  // would leave the pill flush against both edges. Touch targets stay 44px and
  // the per-panel reset hides instead; the top bar's global reset still covers it.
  const resetBtn = compact ? `${btn} hidden sm:flex` : btn;

  // Pointer events must NOT reach the panel underneath. The panel's drag-pan
  // handler calls setPointerCapture() on pointerdown, which retargets the
  // following pointerup to the panel — so the browser fires `click` on the
  // panel instead of on the button, and every control here silently did
  // nothing in Side-by-side and Overlay mode.
  const swallow = (e) => e.stopPropagation();

  return (
    <div
      className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5
                 rounded-full bg-black/75 backdrop-blur px-1 py-0.5 ring-1 ring-white/15 shadow-lg"
      style={{ touchAction: 'manipulation' }}
      onPointerDown={swallow}
      onPointerMove={swallow}
      onPointerUp={swallow}
    >
      <button type="button" className={btn} {...holdOut} aria-label="Zoom out" title="Zoom out (hold to keep zooming)">
        <Minus className="h-5 w-5" />
      </button>
      <button type="button" className={btn} {...holdIn} aria-label="Zoom in" title="Zoom in (hold to keep zooming)">
        <Plus className="h-5 w-5" />
      </button>
      <button
        type="button" className={btn} onClick={onAuto || undefined} disabled={!onAuto}
        aria-label="Auto-match to other photo"
        title={onAuto ? 'Auto-match size & tilt to the other photo' : autoTitle}
      >
        <Crosshair className="h-5 w-5" />
      </button>
      {onReset && (
        <button type="button" className={resetBtn} onClick={onReset} aria-label="Reset this photo" title="Reset this photo">
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Small corner badge showing the manual zoom level, hidden at 100%.
// Kept out of the control pill so it can't crowd the buttons on a narrow
// side-by-side panel; `position` picks whichever corner that mode leaves free.
export function ZoomBadge({ manual, position = 'bottom-left' }) {
  const pct = Math.round((manual?.scale ?? 1) * 100);
  if (pct === 100) return null;
  const posClass = position === 'top-right' ? 'top-2 right-2' : 'bottom-3 left-2';
  return (
    <div className={`absolute ${posClass} z-20 rounded-full bg-black/70 px-1.5 py-0.5
                    text-[10px] font-medium tabular-nums text-white/90 pointer-events-none`}>
      {pct}%
    </div>
  );
}

// Auto-match frequently scales one photo BELOW its panel-fill size (that is the
// point — the bodies end up the same size), which used to expose a hard black
// band at the edges. A blurred copy of the same photo fills the panel behind
// it so the letterboxing reads as deliberate instead of broken.
export function PanelBackdrop({ url }) {
  return (
    <img
      src={url} alt="" aria-hidden="true" draggable={false}
      className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60 pointer-events-none"
    />
  );
}

export function formatMetricLine(weightKg, bodyFat) {
  const parts = [];
  if (weightKg != null && weightKg !== '') parts.push(`${Number(weightKg).toFixed(1)}kg`);
  if (bodyFat != null && bodyFat !== '') parts.push(`${Number(bodyFat).toFixed(1)}%`);
  return parts.join(' · ');
}

export function PhotoLabel({ position, tone, metric, label }) {
  const posClass = position === 'top-right' ? 'top-2 right-2 text-right' : 'top-2 left-2';
  const toneClass = tone === 'accent' ? 'bg-[#FF5C00]/90' : 'bg-black/60';
  return (
    <div className={`absolute ${posClass} px-2 py-1 rounded-md ${toneClass} text-white text-[11px] font-medium leading-tight pointer-events-none`}>
      {metric && <div className="tabular-nums">{metric}</div>}
      <div className="uppercase tracking-wider">{label}</div>
    </div>
  );
}

export function GridOverlay() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
      <line x1="33.33" y1="0" x2="33.33" y2="100" stroke="white" strokeOpacity="0.25" strokeWidth="0.2" />
      <line x1="66.66" y1="0" x2="66.66" y2="100" stroke="white" strokeOpacity="0.25" strokeWidth="0.2" />
      <line x1="0" y1="33.33" x2="100" y2="33.33" stroke="white" strokeOpacity="0.25" strokeWidth="0.2" />
      <line x1="0" y1="66.66" x2="100" y2="66.66" stroke="white" strokeOpacity="0.25" strokeWidth="0.2" />
    </svg>
  );
}

// Canonical frame is in normalized 0..1 coords. We use object-fit: cover on a
// wrapper sized to the frame aspect ratio, then scale+translate the img so the
// bounding box fills the wrapper.
//
// Head anchoring: after the canonical scale, an additional translate(dx, dy)
// is applied so the subject's estimated TOP OF THE HEAD lands at a fixed
// position in the panel (TARGET_HEAD_*_PCT). Both BEFORE and AFTER photos use
// the same target, so the two crowns sit on the same line regardless of
// differences in framing or body proportion. The geometry itself lives in
// photoAligner.computeCanonicalRender so autoMatch can solve against exactly
// the same numbers the renderer uses.
export function canonicalImgStyle(canonical, extraTransform = null) {
  const { cxPct, cyPct, scale, dxPct, dyPct } = computeCanonicalRender(canonical);
  return {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: `${cxPct}% ${cyPct}%`,
    transform: `translate(${dxPct}%, ${dyPct}%) scale(${scale}) ${extraTransform || ''}`,
    transformOrigin: `${cxPct}% ${cyPct}%`,
  };
}
